import { FileLinks, AttachToPayment } from "@/components/PaymentFiles";
import React, { useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { api, formatErr } from "@/lib/api";
import { AddableSelect } from "@/components/AddableSelect";
import { FREQUENCIES, PAYMENT_MODES, fmtAmount, fmtDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, Paperclip, X, FileText, Image as ImageIcon, Download, Trash2, ReceiptText } from "lucide-react";
import { toast } from "sonner";

const ACCEPT_ATTACH = ".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.webp,.gif";
const MAX_ATTACH_MB = 15;

function fileTypeIcon(contentType) {
  if (!contentType) return <FileText className="w-4 h-4 text-slate-500" />;
  if (contentType.startsWith("image/")) return <ImageIcon className="w-4 h-4 text-indigo-600" />;
  return <FileText className="w-4 h-4 text-slate-500" />;
}

function humanSize(bytes) {
  const b = Number(bytes || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

const empty = {
  name: "",
  category: "",
  type: "",
  amount: "",
  currency: "INR",
  frequency: "monthly",
  custom_days: "",
  start_date: new Date(),
  next_due_date: new Date(),
  stop_date: null,
  entity: "",
  is_variable_amount: false,
  account_number: "",
  associated_email: "",
  associated_phone: "",
  payment_mode: "manual",
  notes: "",
  reminder_days_before: 5,
};

const fromObligation = (o) => ({
  ...o,
  amount: o.amount != null ? String(o.amount) : "",
  custom_days: o.custom_days ? String(o.custom_days) : "",
  start_date: parseISO(o.start_date),
  next_due_date: parseISO(o.next_due_date),
  stop_date: o.stop_date ? parseISO(o.stop_date) : null,
  entity: o.entity || "",
  is_variable_amount: !!o.is_variable_amount,
  reminder_days_before: o.reminder_days_before ?? 5,
});

function DatePick({ value, onChange, testid, allowClear }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid={testid} type="button" variant="outline" className="w-full justify-start mono font-normal">
          <CalendarIcon className="w-4 h-4 mr-2" />
          {value ? format(value, "dd MMM yyyy") : "Pick a date"}
          {allowClear && value && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onChange(null); }}
              className="ml-auto text-slate-400 hover:text-rose-600 cursor-pointer"
              aria-label="Clear date"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value || undefined}
          onSelect={(d) => d && onChange(d)}
          initialFocus
          captionLayout="dropdown-buttons"
          fromYear={2000}
          toYear={new Date().getFullYear() + 20}
          defaultMonth={value || new Date()}
        />
      </PopoverContent>
    </Popover>
  );
}

export default function ObligationForm({ open, onOpenChange, editing, prefill, onSaved }) {
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  const [types, setTypes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [payments, setPayments] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPendingFiles([]);
    Promise.all([
      api.get("/categories"),
      api.get("/obligation-types"),
      api.get("/entities"),
    ]).then(([c, t, e]) => {
      setCats(c.data);
      setTypes(t.data);
      setEntities(e.data);
      if (editing) {
        setForm(fromObligation(editing));
      } else if (prefill) {
        setForm({ ...fromObligation(prefill), name: `${prefill.name} (copy)` });
      } else {
        setForm({
          ...empty,
          category: c.data[0]?.name || "",
          type: (t.data.find((x) => x.name === "Recurring") || t.data[0])?.name || "",
        });
        setAttachments([]);
        setPayments([]);
      }
    });
    if (editing) {
      api.get(`/obligations/${editing.id}/attachments`).then(({ data }) => setAttachments(data)).catch(() => setAttachments([]));
      api.get(`/obligations/${editing.id}/payments`).then(({ data }) => setPayments(data)).catch(() => setPayments([]));
    } else {
      setAttachments([]);
      setPayments([]);
    }
  }, [open, editing, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stageFiles = (fileList) => {
    if (!fileList) return;
    const arr = Array.from(fileList);
    const valid = [];
    for (const f of arr) {
      if (f.size > MAX_ATTACH_MB * 1024 * 1024) { toast.error(`${f.name} is over ${MAX_ATTACH_MB} MB`); continue; }
      valid.push(f);
    }
    if (valid.length) setPendingFiles((p) => [...p, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removePending = (idx) => setPendingFiles((p) => p.filter((_, i) => i !== idx));
  const uploadPending = async (obligationId) => {
    for (const f of pendingFiles) {
      const fd = new FormData(); fd.append("file", f);
      try {
        await api.post(`/obligations/${obligationId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) { toast.error(`Failed to upload ${f.name}: ${formatErr(e)}`); }
    }
    setPendingFiles([]);
  };
  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteAttachment = async (id) => {
    try { await api.delete(`/attachments/${id}`); setAttachments((a) => a.filter((x) => x.id !== id)); toast.success("Attachment removed"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const save = async () => {
    if (!form.name || !form.category || !form.type) {
      toast.error("Please fill name, category, and type"); return;
    }
    // Amount is required only when not variable AND user entered a number expectation
    if (!form.is_variable_amount && form.amount !== "" && parseFloat(form.amount) <= 0) {
      toast.error("Amount must be greater than 0"); return;
    }
    if (form.frequency === "custom" && !form.custom_days) {
      toast.error("Please enter custom interval in days"); return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      type: form.type,
      amount: form.amount === "" ? null : parseFloat(form.amount),
      currency: form.currency,
      frequency: form.frequency,
      custom_days: form.custom_days ? parseInt(form.custom_days) : null,
      start_date: format(form.start_date, "yyyy-MM-dd"),
      next_due_date: format(form.next_due_date, "yyyy-MM-dd"),
      stop_date: form.stop_date ? format(form.stop_date, "yyyy-MM-dd") : null,
      entity: form.entity || null,
      is_variable_amount: !!form.is_variable_amount,
      account_number: form.account_number || null,
      associated_email: form.associated_email || null,
      associated_phone: form.associated_phone || null,
      payment_mode: form.payment_mode,
      notes: form.notes || null,
      reminder_days_before: parseInt(form.reminder_days_before) || 5,
    };
    try {
      let obligationId;
      if (editing) { await api.patch(`/obligations/${editing.id}`, payload); obligationId = editing.id; }
      else { const { data } = await api.post("/obligations", payload); obligationId = data.id; }
      if (pendingFiles.length) await uploadPending(obligationId);
      toast.success(editing ? "Obligation updated" : "Obligation created");
      onSaved?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="obligation-form-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="obligation-form-title">{editing ? "Edit obligation" : prefill ? "Duplicate obligation" : "New obligation"}</DialogTitle>
          <DialogDescription>{prefill && !editing ? "A copy with the same details. Nothing is saved until you click Create — payments and attachments are not copied." : "Track any recurring expense, EMI, loan, or liability."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
            <Input data-testid="form-name" className="mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Home loan EMI · HDFC" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
            <AddableSelect value={form.type} onChange={(v) => set("type", v)} items={types} endpoint="/obligation-types" noun="Type" testid="form-type"
              onCreated={(d) => setTypes((p) => [...p, d])} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Category</Label>
            <AddableSelect value={form.category} onChange={(v) => set("category", v)} items={cats} endpoint="/categories" noun="Category" testid="form-category"
              onCreated={(d) => setCats((p) => [...p, d])} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Entity (payer/vendor/bank)</Label>
            <AddableSelect value={form.entity} onChange={(v) => set("entity", v)} items={entities} endpoint="/entities" noun="Entity" testid="form-entity"
              placeholder="Select an entity (optional)" noneLabel="— No entity —" onCreated={(d) => setEntities((p) => [...p, d])} />
            {entities.length === 0 && (
              <p className="text-[11px] text-slate-500 mt-1">Use &ldquo;Add new entity…&rdquo; in the list — e.g. HDFC Bank, LIC, Airtel.</p>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Amount {form.is_variable_amount && <span className="normal-case tracking-normal text-amber-700">· variable</span>}</Label>
            <div className="flex gap-2 mt-1">
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="form-currency" className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="form-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mono placeholder:text-[11px] placeholder:font-sans min-w-0" placeholder={form.is_variable_amount ? "Optional (asked each time)" : "Leave blank for reminder"} />
            </div>
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox data-testid="form-variable-amount" checked={form.is_variable_amount} onCheckedChange={(v) => set("is_variable_amount", !!v)} />
              Variable amount — I&apos;ll enter the actual amount each time I mark it paid
            </label>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Payment mode</Label>
            <Select value={form.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
              <SelectTrigger data-testid="form-payment-mode" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Frequency</Label>
            <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger data-testid="form-frequency" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.frequency === "custom" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Interval (days)</Label>
              <Input data-testid="form-custom-days" type="number" min="1" value={form.custom_days} onChange={(e) => set("custom_days", e.target.value)} className="mt-1 mono" />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Start date</Label>
            <div className="mt-1"><DatePick testid="form-start-date" value={form.start_date} onChange={(d) => set("start_date", d)} /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Next due date</Label>
            <div className="mt-1"><DatePick testid="form-next-due-date" value={form.next_due_date} onChange={(d) => set("next_due_date", d)} /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Stop date <span className="normal-case tracking-normal text-slate-400">· optional</span></Label>
            <div className="mt-1"><DatePick testid="form-stop-date" value={form.stop_date} onChange={(d) => set("stop_date", d)} allowClear /></div>
            <p className="text-[11px] text-slate-500 mt-1">After this date, no more due dates will be generated.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Account / Reference number</Label>
            <Input data-testid="form-account" className="mt-1 mono" value={form.account_number || ""} onChange={(e) => set("account_number", e.target.value)} placeholder="e.g. HDFC-HL-102938" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Reminder — days before</Label>
            <Input data-testid="form-reminder-days" type="number" min="0" max="30" value={form.reminder_days_before} onChange={(e) => set("reminder_days_before", e.target.value)} className="mt-1 mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Associated email</Label>
            <Input data-testid="form-associated-email" type="email" className="mt-1 mono" value={form.associated_email || ""} onChange={(e) => set("associated_email", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Associated phone</Label>
            <Input data-testid="form-associated-phone" className="mt-1 mono" value={form.associated_phone || ""} onChange={(e) => set("associated_phone", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea data-testid="form-notes" className="mt-1" rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
          </div>

          {/* Payment history — only in edit mode */}
          {editing && (
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <ReceiptText className="w-3.5 h-3.5" /> Payment history
                </Label>
                <div className="text-[11px] text-slate-500 mono">{payments.length} payments</div>
              </div>
              {payments.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-md p-3 text-xs text-slate-500">
                  No payments recorded yet. Mark this obligation as paid to record a payment.
                </div>
              ) : (
                <ul className="border border-slate-200 rounded-md divide-y divide-slate-200 overflow-hidden">
                  {payments.map((p) => (
                    <li key={p.id} data-testid={`payment-row-${p.id}`} className="px-3 py-2 flex items-center gap-3 bg-white">
                      <CheckCircle2Small />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium mono">{fmtDate(p.payment_date)}</div>
                        {p.notes && <div className="text-[11px] text-slate-500 truncate">{p.notes}</div>}
                        <FileLinks files={p.attachments} testid={`payment-row-${p.id}`} />
                      </div>
                      <div className="mono text-sm">{p.amount != null ? fmtAmount(p.amount, editing.currency) : <span className="text-slate-400">—</span>}</div>
                      <AttachToPayment obligationId={editing.id} paymentId={p.id} testid={`payment-attach-${p.id}`}
                        onAdded={() => api.get(`/obligations/${editing.id}/payments`).then(({ data }) => setPayments(data)).catch(() => {})} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Attachments */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" /> Attachments
              </Label>
              <div className="text-[11px] text-slate-500">PDF · DOC · XLS · TXT · Images · up to {MAX_ATTACH_MB} MB each</div>
            </div>
            <div className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50/40">
              <input ref={fileInputRef} data-testid="attachment-file-input" type="file" multiple accept={ACCEPT_ATTACH} onChange={(e) => stageFiles(e.target.files)} className="hidden" />
              <Button data-testid="attachment-add-btn" type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="gap-2">
                <Paperclip className="w-3.5 h-3.5" /> Add files
              </Button>
              {attachments.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {attachments.map((a) => (
                    <li key={a.id} data-testid={`attachment-row-${a.id}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
                      {fileTypeIcon(a.content_type)}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{a.filename}</div>
                        <div className="text-[11px] text-slate-500 mono">{humanSize(a.size)} · {a.content_type}</div>
                      </div>
                      <Button data-testid={`attachment-download-${a.id}`} type="button" size="icon" variant="ghost" onClick={() => downloadAttachment(a)}><Download className="w-4 h-4" /></Button>
                      <Button data-testid={`attachment-delete-${a.id}`} type="button" size="icon" variant="ghost" onClick={() => deleteAttachment(a.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </li>
                  ))}
                </ul>
              )}
              {pendingFiles.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {pendingFiles.map((f, i) => (
                    <li key={`${f.name}-${i}`} data-testid={`pending-file-${i}`} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                      {fileTypeIcon(f.type)}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{f.name}</div>
                        <div className="text-[11px] text-amber-800 mono">{humanSize(f.size)} · pending upload</div>
                      </div>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removePending(i)}><X className="w-4 h-4" /></Button>
                    </li>
                  ))}
                </ul>
              )}
              {attachments.length === 0 && pendingFiles.length === 0 && (
                <div className="text-xs text-slate-500 mt-3">No files attached. Add supporting documents like loan agreements, receipts, or bill copies.</div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button data-testid="form-cancel" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="form-save" disabled={saving} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">
            {saving ? "Saving…" : editing ? "Save changes" : "Create obligation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckCircle2Small() {
  return (
    <span className="w-6 h-6 rounded-full bg-emerald-50 border border-emerald-200 grid place-items-center shrink-0">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
    </span>
  );
}
